// =============================================================================
// Backend client — wrapper fetch() gọi Hono backend.
//
// Trước đây gọi qua `supabase.functions.invoke('shipping-quote', {...})` →
// Supabase Edge Functions. Sau khi migrate, gọi trực tiếp tới backend URL
// (env.backendUrl) với JWT user trong Authorization header.
//
// Pattern dùng:
//   const { data, error } = await backendInvoke<ResponseShape>('shipping-quote', { ... })
//
// Auto-attach Authorization từ session Supabase hiện tại — caller không cần
// quản lý token thủ công. Nếu chưa login → throw lỗi.
// =============================================================================

import { env } from './env'
import { getSupabase } from './supabase'

export type BackendError = {
  error: string
  detail?: string
  hint?: string
}

export type BackendResponse<T> =
  | { data: T; error: null }
  | { data: null; error: BackendError }

async function getAuthHeader(): Promise<string> {
  const { data } = await getSupabase().auth.getSession()
  const token = data.session?.access_token
  if (!token) {
    throw new Error('Chưa đăng nhập — không gọi được backend.')
  }
  return `Bearer ${token}`
}

function ensureBackendUrl(): string {
  if (!env.backendUrl) {
    throw new Error(
      'VITE_BACKEND_URL chưa cấu hình. Set ở .env.local (dev) hoặc Vercel env (prod).',
    )
  }
  return env.backendUrl.replace(/\/$/, '')
}

// JSON POST wrapper — return { data, error } shape giống Supabase invoke để
// caller migrate dễ.
export async function backendInvoke<T>(
  path: string,
  body: unknown,
): Promise<BackendResponse<T>> {
  const auth = await getAuthHeader()
  const url = `${ensureBackendUrl()}/${path.replace(/^\//, '')}`
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: auth,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body ?? {}),
    })

    const contentType = res.headers.get('content-type') ?? ''
    if (!res.ok) {
      let errBody: BackendError = { error: `HTTP ${res.status}` }
      if (contentType.includes('application/json')) {
        try {
          errBody = (await res.json()) as BackendError
        } catch {
          // ignore
        }
      } else {
        const text = await res.text().catch(() => '')
        if (text) errBody = { error: text.slice(0, 300) }
      }
      return { data: null, error: errBody }
    }

    const data = (await res.json()) as T
    return { data, error: null }
  } catch (err) {
    return {
      data: null,
      error: {
        error: 'network_error',
        detail: err instanceof Error ? err.message : 'unknown',
      },
    }
  }
}

// Generic request — hỗ trợ mọi HTTP method (GET/POST/PATCH/PUT/DELETE) + query.
// Dùng cho module Pancake (REST đầy đủ). Trả về { data, error } như backendInvoke.
export async function backendRequest<T>(
  method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE',
  path: string,
  options: { body?: unknown; query?: Record<string, string | number | undefined> } = {},
): Promise<BackendResponse<T>> {
  const auth = await getAuthHeader()
  let url = `${ensureBackendUrl()}/${path.replace(/^\//, '')}`
  if (options.query) {
    const qs = new URLSearchParams()
    for (const [k, v] of Object.entries(options.query)) {
      if (v !== undefined && v !== '') qs.set(k, String(v))
    }
    const s = qs.toString()
    if (s) url += `?${s}`
  }
  try {
    const res = await fetch(url, {
      method,
      headers: {
        Authorization: auth,
        ...(options.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      },
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    })
    const contentType = res.headers.get('content-type') ?? ''
    if (!res.ok) {
      let errBody: BackendError = { error: `HTTP ${res.status}` }
      if (contentType.includes('application/json')) {
        try {
          errBody = (await res.json()) as BackendError
        } catch {
          // ignore
        }
      }
      return { data: null, error: errBody }
    }
    const data = (await res.json()) as T
    return { data, error: null }
  } catch (err) {
    return {
      data: null,
      error: { error: 'network_error', detail: err instanceof Error ? err.message : 'unknown' },
    }
  }
}

// Binary fetch — dùng cho shipping-label (PDF stream). Trả Response để caller
// xử lý blob/objectURL.
export async function backendFetch(path: string, body: unknown): Promise<Response> {
  const auth = await getAuthHeader()
  const url = `${ensureBackendUrl()}/${path.replace(/^\//, '')}`
  return fetch(url, {
    method: 'POST',
    headers: {
      Authorization: auth,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body ?? {}),
  })
}

// POST FormData (multipart) — cho upload file. KHÔNG set Content-Type (browser tự thêm boundary).
export async function backendUploadForm(path: string, form: FormData): Promise<Response> {
  const auth = await getAuthHeader()
  const url = `${ensureBackendUrl()}/${path.replace(/^\//, '')}`
  return fetch(url, { method: 'POST', headers: { Authorization: auth }, body: form })
}

// GET raw Response (cho binary/blob) kèm auth.
export async function backendGet(path: string): Promise<Response> {
  const auth = await getAuthHeader()
  const url = `${ensureBackendUrl()}/${path.replace(/^\//, '')}`
  return fetch(url, { method: 'GET', headers: { Authorization: auth } })
}
